var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import Loading from '../Loading';
import { getParentOrigin, postParentWindowMessage, } from '../../../utils/post-message';
import { addFeatureFlag, removeFeatureFlag, setContextValue, } from '../../../tracking/datadog';
import useIntegration from './hooks/useIntegration';
import { useGetHeaders } from './hooks/useGetHeaders';
import useFireClientEvent from '../../../hooks/useFireClientEvent';
import { useMessageListener } from './hooks/useMessageListener';
import { useAppSelector } from '../../../state';
import { selectExperiments, selectLinkTokenPayload, selectDevicePlatform, selectModalSessionId, selectUniqueUserId, } from '../../../state/slices/slice-link-params';
import { useGetLocalizedString } from '../../../hooks/useGetLocalizedString';
import useAdditionalParameters from './hooks/useAdditionalParameters';
import { CERIDIAN_FINAL_DEST_REGEXP } from './integrations/useCeridian';
import { VA_BENEFITS_FINAL_DEST_REGEXP } from './integrations/useVaBenefits';
import { LIBERTY_TAX_FINAL_DEST_REGEXP } from './integrations/useLibertyTax';
import { getEdgeCurrentLocation } from './integration-utils';
import DeviceBasedPrimer from '../DeviceBasedPrimer';
import { selectLoadedPlatform } from '../../../state/slices/slice-link-api';
import { getEdgeNativeSandboxUrl } from '../../../utils/edge-native-sandbox-url';
import sendScreenshotData from '../../../tracking/artifacts';
import { useArtifactsInterval } from '../../../hooks/useArtifactsInterval';
import { useAreScreenshotsEnabled } from '../../../hooks/useAreScreenshotsEnabled';
import { TOAST_FINAL_DEST_REGEXP } from './integrations/useToast';
import { PAYCOM_FINAL_DEST_REGEXP } from './integrations/usePaycom';
export var CRITERIA_MAP = {
    'auth=adp': { type: 'cookie_key', value: 'SMSESSION' },
    'auth=paycom': { type: 'url', value: PAYCOM_FINAL_DEST_REGEXP },
    'auth=onlyFans': { type: 'cookie_key', value: 'st' },
    'auth=sandbox': { type: 'cookie_key', value: 'X_PW_SANDBOX_AUTH' },
    'auth=single_page_application_test': { type: 'cookie_key', value: 'x' },
    'auth=amazon': { type: 'cookie_key', value: 'SMSESSION' },
    'auth=paylocity': { type: 'cookie_key', value: 'Paylocity.WebPay.Cookies' },
    'auth=paycor': { type: 'cookie_key', value: 'paycorAuth' },
    'auth=walmart': { type: 'cookie_key', value: 'adp_user_profile' },
    'auth=justworks': { type: 'cookie_key', value: 'jw_gsid_prod' },
    'auth=irs': { type: 'cookie_key', value: 'PAPROD.SADIApp-www4' },
    'auth=hrblock': { type: 'cookie_key', value: '_mytax_s' },
    'auth=workday': { type: 'setos_request' },
    'auth=workday-alt': {
        type: 'cookie_key_value',
        key: 'UserSignedIn',
        value: '1',
    },
    'auth=paychex': { type: 'cookie_key', value: '_pyxsession' },
    'auth=ultipro': { type: 'cookie_key', value: 'loginToken' },
    'auth=ultipro-okta': { type: 'cookie_key', value: /proximity_[a-z0-9]+/ },
    'auth=gusto': { type: 'cookie_key', value: 'remember_user_token' },
    'auth=kroger': {
        type: 'cookie_key_value',
        key: 'OAMAuthnHintCookie',
        value: /^1$/,
    },
    'auth=ceridian': { type: 'url', value: CERIDIAN_FINAL_DEST_REGEXP },
    'auth=socialsecurity': { type: 'cookie_key', value: 'MYMICN' },
    'auth=taxslayer': {
        type: 'cookie_key_value',
        key: 'X-Client-Id',
        value: 'ts_my_account_react_client',
    },
    'auth=taxact': { type: 'cookie_key', value: 'tauser' },
    'auth=libertytax': { type: 'url', value: LIBERTY_TAX_FINAL_DEST_REGEXP },
    'auth=jacksonhewitt': { type: 'cookie_key', value: 'jhPid' },
    'auth=rippling': { type: 'cookie_key', value: 'cf_clearance' },
    'auth=publix': { type: 'cookie_key', value: '.AspNet.Cookies' },
    'auth=vabenefits': { type: 'url', value: VA_BENEFITS_FINAL_DEST_REGEXP },
    'auth=persona-test': { type: 'setos_request' },
    'auth=doorDash': { type: 'setos_request' },
    'auth=dailyPay': { type: 'setos_request' },
    'auth=toast': { type: 'url', value: TOAST_FINAL_DEST_REGEXP },
    'auth=kaiserPermanente': {
        type: 'cookie_key',
        value: 'PA.WS_HRCONNECT_LAN_PROD_001',
    },
};
// Context: https://pinwheel.atlassian.net/browse/CORE-1837
var IOS_USER_AGENT_MAP = {
    'auth=persona-test': 'UA-name:iOSDasherWebView; client-version: 2.345.0.215458;',
    'auth=doorDash': 'UA-name:iOSDasherWebView; client-version: 2.345.0.215458;',
};
var ANDROID_USER_AGENT_MAP = {
    'auth=persona-test': 'UA-name:AndroidDasherWebView; client-version:8003119',
    'auth=doorDash': 'UA-name:AndroidDasherWebView; client-version:8003119',
};
export var getCustomUserAgentMap = function (platform) {
    return platform === 'android' ? ANDROID_USER_AGENT_MAP : IOS_USER_AGENT_MAP;
};
// Context: https://pinwheel.atlassian.net/browse/CORE-1837
export var IOS_INITIAL_HEADERS_MAP = {
    'auth=persona-test': [{ name: 'Referer', value: 'https://doordash.com/' }],
    'auth=doorDash': [{ name: 'Referer', value: 'https://doordash.com/' }],
};
export var ANDROID_INITIAL_HEADERS_MAP = {
    'auth=persona-test': [
        { name: 'Referer', value: 'https://doordash.com/' },
        { name: 'X-Requested-With', value: 'com.doordash.driverapp' },
        {
            name: 'sec-ch-ua',
            value: '"Android WebView";v="131", "Chromium";v="131", "Not_A Brand";v="24"',
        },
        { name: 'sec-ch-ua-mobile', value: '?1' },
        { name: 'sec-ch-ua-platform', value: '"Android"' },
        { name: 'Sec-Fetch-Dest', value: 'iframe' },
        { name: 'Sec-Fetch-Mode', value: 'navigate' },
        { name: 'Sec-Fetch-Site', value: 'cross-site' },
        { name: 'Sec-Fetch-User', value: '?1' },
        { name: 'Upgrade-Insecure-Requests', value: '1' },
    ],
    'auth=doorDash': [
        { name: 'Referer', value: 'https://doordash.com/' },
        { name: 'X-Requested-With', value: 'com.doordash.driverapp' },
        {
            name: 'sec-ch-ua',
            value: '"Android WebView";v="131", "Chromium";v="131", "Not_A Brand";v="24"',
        },
        { name: 'sec-ch-ua-mobile', value: '?1' },
        { name: 'sec-ch-ua-platform', value: '"Android"' },
        { name: 'Sec-Fetch-Dest', value: 'iframe' },
        { name: 'Sec-Fetch-Mode', value: 'navigate' },
        { name: 'Sec-Fetch-Site', value: 'cross-site' },
        { name: 'Sec-Fetch-User', value: '?1' },
        { name: 'Upgrade-Insecure-Requests', value: '1' },
    ],
};
export var getCustomInitialHeadersMap = function (platform) {
    return platform === 'android'
        ? ANDROID_INITIAL_HEADERS_MAP
        : IOS_INITIAL_HEADERS_MAP;
};
var getCookiesFromDownEvent = function (x) {
    if (x.payload.setos)
        return x.payload.setos;
    if ('cookies' in x.payload)
        return x.payload.cookies;
    return [];
};
function checkIsLoggedIn(criteria, payload, event) {
    var _a;
    return __awaiter(this, void 0, void 0, function () {
        var location_1;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (criteria.type === 'cookie_key' || criteria.type === 'cookie_key_value') {
                        return [2 /*return*/, payload.cookies.some(function (x) {
                                if (criteria.type === 'cookie_key_value') {
                                    if (criteria.value instanceof RegExp) {
                                        return criteria.key === x.name && criteria.value.test(x.value);
                                    }
                                    return criteria.key === x.name && x.value.includes(criteria.value);
                                }
                                if (typeof criteria.value === 'string') {
                                    return criteria.value === x.name;
                                }
                                return criteria.value.test(x.name);
                            })];
                    }
                    if (criteria.type === 'setos_request' && event.eventName === 'setos_response')
                        return [2 /*return*/, true];
                    if (!(criteria.type === 'url' &&
                        event.eventName === 'window_location_load_complete')) return [3 /*break*/, 2];
                    // Check for "location" key.
                    // Older mobile SDK version do not send this with "window_location_load_complete" events
                    if ('location' in event.payload && event.payload.location) {
                        return [2 /*return*/, (_a = event.payload.location) === null || _a === void 0 ? void 0 : _a.match(criteria.value)];
                    }
                    return [4 /*yield*/, getEdgeCurrentLocation()];
                case 1:
                    location_1 = _b.sent();
                    return [2 /*return*/, location_1.match(criteria.value)];
                case 2: return [2 /*return*/, false];
            }
        });
    });
}
function TemplateEdgeNative(_a) {
    var _b, _c;
    var submitStep = _a.submitStep, webviewUrl = _a.webviewUrl, fieldKey = _a.fieldKey, successCriteria = _a.successCriteria;
    var getLocalizedString = useGetLocalizedString();
    var _d = useState(getLocalizedString('EDGE_NATIVE_LOADING')), loadingText = _d[0], setLoadingText = _d[1];
    var _e = useState(false), isWebviewVisible = _e[0], setIsWebviewVisible = _e[1];
    var _f = useState(false), hasSubmitted = _f[0], setHasSubmitted = _f[1];
    var getAdditionalParameters = useAdditionalParameters().getAdditionalParameters;
    var linkTokenPayload = useAppSelector(selectLinkTokenPayload);
    var platform = useAppSelector(selectLoadedPlatform);
    var devicePlatform = useAppSelector(selectDevicePlatform) || 'ios';
    var isNativeLinkScreenshotsEnabled = useAreScreenshotsEnabled();
    var webviewUrlWithPossibleSandboxParams = getEdgeNativeSandboxUrl(linkTokenPayload, webviewUrl, platform);
    var _g = useArtifactsInterval(), startArtifactsInterval = _g.startArtifactsInterval, clearArtifactsInterval = _g.clearArtifactsInterval;
    useEffect(function () {
        addFeatureFlag('ff-edge');
        setContextValue({ isEdgeNative: true });
    }, []);
    // Tell SDK to open webview
    useEffect(function () {
        var criteria = CRITERIA_MAP[successCriteria];
        if (!criteria) {
            datadogLogs.logger.error('invalid_success_criteria_key', {
                success_criteria: successCriteria,
            });
        }
        var customUserAgentMap = getCustomUserAgentMap(devicePlatform);
        var customInitialHeadersMap = getCustomInitialHeadersMap(devicePlatform);
        var customUserAgent = customUserAgentMap[successCriteria];
        var customInitialHeaders = customInitialHeadersMap[successCriteria];
        postParentWindowMessage({
            type: 'PINWHEEL_INTERNAL_COMM_UP',
            eventName: 'edge_request',
            payload: __assign(__assign({ webviewUrl: webviewUrlWithPossibleSandboxParams, successCriteria: 
                // Need to do this check as iOS 2.4.0 only accepts strings for "value"
                'value' in criteria && typeof criteria.value === 'string'
                    ? criteria
                    : __assign(__assign({}, criteria), { value: '' }) }, (customUserAgent && { customUserAgent: customUserAgent })), (customInitialHeaders && { customInitialHeaders: customInitialHeaders })),
        });
    }, [webviewUrlWithPossibleSandboxParams, successCriteria, devicePlatform]);
    useEffect(function () {
        if (isNativeLinkScreenshotsEnabled) {
            startArtifactsInterval();
            return function () { return clearArtifactsInterval(); };
        }
        return undefined;
    }, [
        startArtifactsInterval,
        clearArtifactsInterval,
        isNativeLinkScreenshotsEnabled,
    ]);
    var headers = useGetHeaders(isWebviewVisible);
    var translateAndSubmit = useCallback(function (payload) {
        var _a;
        if (hasSubmitted)
            return;
        setHasSubmitted(true);
        var submission = (_a = {},
            _a[fieldKey] = encodeURIComponent(JSON.stringify({
                cookies: payload.cookies,
                headers: headers,
                additionalParameters: getAdditionalParameters(),
            })),
            _a);
        submitStep(submission);
    }, [fieldKey, headers, submitStep, hasSubmitted, getAdditionalParameters]);
    var modalSessionId = (_b = useAppSelector(selectModalSessionId)) !== null && _b !== void 0 ? _b : '';
    var uniqueUserId = (_c = useAppSelector(selectUniqueUserId)) !== null && _c !== void 0 ? _c : '';
    if (modalSessionId === '')
        datadogLogs.logger.info("No modal session id");
    // Event handler for messages from parent SDK
    var eventHandlerDown = useMemo(function () {
        return function (_a) {
            var data = _a.data, origin = _a.origin;
            if ((data === null || data === void 0 ? void 0 : data.type) !== 'PINWHEEL_INTERNAL_COMM_DOWN')
                return;
            var parentOrigin = getParentOrigin();
            if (parentOrigin && origin !== parentOrigin) {
                datadogLogs.logger.warn("Link received 'PINWHEEL_INTERNAL_COMM_DOWN' message from an untrusted origin (".concat(origin, ")"));
                return;
            }
            if (data.eventName === 'go_back') {
                postParentWindowMessage({
                    type: 'PINWHEEL_INTERNAL_COMM_UP',
                    eventName: 'browser_close',
                });
                clearArtifactsInterval();
                removeFeatureFlag('ff-edge');
                setContextValue({ isEdgeNative: false });
                setLoadingText('');
            }
            else if (data.eventName === 'edge_response') {
                translateAndSubmit(data.payload);
            }
            else if (data.eventName === 'window_location_load_complete' ||
                data.eventName === 'window_location_load_start' ||
                data.eventName === 'setos_response') {
                var criteria = CRITERIA_MAP[successCriteria];
                var payload_1 = { cookies: getCookiesFromDownEvent(data) };
                void checkIsLoggedIn(criteria, payload_1, data).then(function (isLoggedIn) {
                    if (isLoggedIn && !payload_1.cookies.length) {
                        datadogLogs.logger.error('client_side_error_no_cookies', {
                            eventDataWithError: data,
                        });
                    }
                    if (isLoggedIn) {
                        translateAndSubmit(payload_1);
                        postParentWindowMessage({
                            type: 'PINWHEEL_INTERNAL_COMM_UP',
                            eventName: 'browser_close',
                        });
                    }
                });
            }
            else if (data.eventName === 'analytics_response') {
                sendScreenshotData(modalSessionId, uniqueUserId, data.payload.data.result);
            }
        };
    }, [
        successCriteria,
        translateAndSubmit,
        modalSessionId,
        uniqueUserId,
        clearArtifactsInterval,
    ]);
    useMessageListener(eventHandlerDown);
    var fireClientEvent = useFireClientEvent();
    // Event handler for messages to parent SDK
    var eventHandlerUp = useMemo(function () {
        return function (_a) {
            var data = _a.data;
            if ((data === null || data === void 0 ? void 0 : data.type) !== 'PINWHEEL_INTERNAL_COMM_UP')
                return;
            if ((data === null || data === void 0 ? void 0 : data.eventName) !== 'browser_visibility')
                return;
            if (data.payload.visible)
                setIsWebviewVisible(true);
        };
    }, []);
    useMessageListener(eventHandlerUp);
    useEffect(function () {
        if (isWebviewVisible)
            fireClientEvent('input_required');
    }, [fireClientEvent, isWebviewVisible]);
    var IntegrationComponent = useIntegration(successCriteria);
    return (React.createElement(React.Fragment, null,
        React.createElement(IntegrationComponent, null),
        React.createElement(Loading, { text: loadingText })));
}
function TemplateEdgeNativeContainer(props) {
    var linkTokenPayload = useAppSelector(selectLinkTokenPayload);
    var experiments = useAppSelector(selectExperiments);
    var _a = useState(
    // If the intro screen was skipped, we still have legal requirements to show
    // the ToS links before the user logs in, and thus we show the device-based
    // intro/primer screen which includes them. However, for the app clip flow,
    // the app clip primer screen (before the app clip is launched) includes the
    // ToS links, and thus the intro/primer screen isn't needed here.
    (linkTokenPayload.skipIntroScreen &&
        linkTokenPayload.mode === 'production' &&
        !linkTokenPayload.isAppClip) ||
        // CORE-1560: Even though we don't *legally* need to show the primer/intro
        // screen here if ToS links were shown prior, we want to test if showing
        // it anyways would provide a better UX that boosts conversion.
        experiments.alwaysShowDeviceBasedPrimer), showIntro = _a[0], setShowIntro = _a[1];
    return showIntro ? (React.createElement(DeviceBasedPrimer, { onContinue: function () { return setShowIntro(false); } })) : (React.createElement(TemplateEdgeNative, __assign({}, props)));
}
export default TemplateEdgeNativeContainer;
